import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useGetBorrowerByIdQuery, useUpdateBorrowerUserMutation, useUpdateUserMutation } from "../../services/borrowerUserApiSlice";
import { 
  User, Mail, Phone, MapPin, Check, Calendar, Briefcase, 
  CreditCard, Building, Hash, UserCircle, Shield, Edit2,
  Save, X, Eye, EyeOff, Globe, BadgeCheck
} from 'lucide-react';

const Profile = () => {
  const { user: authUser, handleLogin } = useAuth();
  const borrowerId = authUser?.borrowerId || authUser?.id || authUser?.userId || null;
  const [editMode, setEditMode] = useState(false);
  const [showSaveMessage, setShowSaveMessage] = useState(false);
  const [activeTab, setActiveTab] = useState("personal");
  const [showPassword, setShowPassword] = useState(false);

  const { data: borrowerData, isLoading, isError, refetch } = useGetBorrowerByIdQuery(borrowerId, { skip: !borrowerId });
  const [updateBorrowerUser, { isLoading: isUpdatingBorrower }] = useUpdateBorrowerUserMutation();
  const [updateUser, { isLoading: isUpdatingUser }] = useUpdateUserMutation();

  const [user, setUser] = useState({
    userName: "",
    email: "",
    phone: "",
    status: "",
    firstName: "",
    lastName: "",
    nic: "",
    address: "",
    dob: "",
    occupation: "",
    bankName: "",
    accountNumber: "",
    ifscCode: "",
  });

  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (borrowerData) {
      const b = borrowerData;
      setUser({
        userName: authUser?.userName || b.userName || "",
        email: b.email || authUser?.email || "",
        phone: b.phone || authUser?.phone || "",
        status: b.status || authUser?.status || "Active",
        firstName: b.firstName || "",
        lastName: b.lastName || "",
        nic: b.nic || "",
        address: b.address || "",
        dob: b.dob || "",
        occupation: b.occupation || "",
        bankName: b.bankName || "",
        accountNumber: b.accountNumber || "",
        ifscCode: b.ifscCode || "",
      });
    } else if (authUser) {
      // Fallback to auth user
      setUser(prev => ({
        ...prev,
        userName: authUser.userName || "",
        email: authUser.email || "",
        phone: authUser.phone || "",
        status: authUser.status || "Active",
      }));
    }
  }, [borrowerData, authUser]);

  const validateForm = () => {
    const errors = {};
    if (editMode) {
      // First Name validation - required
      if (!user.firstName || user.firstName.trim() === '') {
        errors.firstName = "First name is required";
      }
      // Last Name validation - required
      if (!user.lastName || user.lastName.trim() === '') {
        errors.lastName = "Last name is required";
      }
      // Address validation - required
      if (!user.address || user.address.trim() === '') {
        errors.address = "Address is required";
      }
      // Username validation - required
      if (!user.userName || user.userName.trim() === '') {
        errors.userName = "Username is required";
      }
      // Email validation - required and format
      if (!user.email || user.email.trim() === '') {
        errors.email = "Email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user.email)) {
        errors.email = "Invalid email address";
      }
      // Phone validation - required and format
      if (!user.phone || user.phone.trim() === '') {
        errors.phone = "Phone number is required";
      } else if (!/^0\d{9}$/.test(user.phone.trim())) {
        errors.phone = "Phone number must be exactly 10 digits starting with 0 (e.g., 0781234567)";
      }
      // NIC validation - required and format
      if (!user.nic || user.nic.trim() === '') {
        errors.nic = "NIC is required";
      } else if (!/^(?:\d{9}[VX]|\d{12})$/.test(user.nic.trim())) {
        errors.nic = "Invalid NIC format. Please use 9 digits followed by V/X (old format) or 12 digits (new format)";
      }
      // Date of Birth validation - required
      if (!user.dob || user.dob.trim() === '') {
        errors.dob = "Date of birth is required";
      }
    }
    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUser({ ...user, [name]: value });
    
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();

    if (!borrowerId) {
      alert("Please log in to update profile.");
      return;
    }

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    // Prepare payloads
    const borrowerPayload = {
      borrowerId: Number(borrowerId),
      firstName: user.firstName || null,
      lastName: user.lastName || null,
      nic: user.nic || null,
      address: user.address || null,
      dob: user.dob || null,
      occupation: user.occupation || null,
      // bankName: user.bankName || null,
      // accountNumber: user.accountNumber || null,
      // ifscCode: user.ifscCode || null,
    };

    const userPayload = {
      userId: authUser?.userId || authUser?.id,
      phone: user.phone || null,
      email: user.email || null,
      userName: user.userName || null,
      status: user.status || "Active"
    };

    try {
      await updateBorrowerUser(borrowerPayload).unwrap();
      await updateUser(userPayload).unwrap();

      // Update auth user details
      const updatedAuthUser = { 
        ...authUser, 
        email: userPayload.email || authUser.email, 
        phone: userPayload.phone || authUser.phone,
        userName: userPayload.userName || authUser.userName
      };
      localStorage.setItem('user', JSON.stringify(updatedAuthUser));
      handleLogin(localStorage.getItem('token'), updatedAuthUser);

      // Show success message
      setShowSaveMessage(true);
      setEditMode(false);
      refetch();
      
      setTimeout(() => setShowSaveMessage(false), 3000);
      
    } catch (err) {
      console.error("Profile update error:", err);
      const msg = err?.data?.Result || err?.data?.Message || err?.error || err?.message;
      
      // Handle specific duplicate errors
      const errorMap = {
        'Phone already registered by another user': 'phone',
        'Email already registered by another user': 'email',
        'Username already exists for another user': 'userName'
      };

      if (msg && Object.keys(errorMap).includes(msg)) {
        const fieldName = errorMap[msg];
        setFormErrors(prev => ({
          ...prev,
          [fieldName]: msg
        }));
      } else {
        alert(msg || "Failed to update profile. Please try again.");
      }
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setFormErrors({});
    // Reset form with original data
    if (borrowerData) {
      const b = borrowerData;
      setUser({
        userName: authUser?.userName || b.userName || "",
        email: b.email || authUser?.email || "",
        phone: b.phone || authUser?.phone || "",
        status: b.status || authUser?.status || "Active",
        firstName: b.firstName || "",
        lastName: b.lastName || "",
        nic: b.nic || "",
        address: b.address || "",
        dob: b.dob || "",
        occupation: b.occupation || "",
        bankName: b.bankName || "",
        accountNumber: b.accountNumber || "",
        ifscCode: b.ifscCode || "",
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-4 sm:p-6 lg:p-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col items-center justify-center min-h-[60vh]">
            <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-6"></div>
            <h2 className="text-xl font-semibold text-gray-700">Loading profile...</h2>
            <p className="text-gray-500 mt-2">Please wait while we fetch your information</p>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-4 sm:p-6 lg:p-8">
        <div className="max-w-6xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-red-900 mb-2">Unable to Load Profile</h2>
            <p className="text-red-700 mb-6">We encountered an error loading your profile information.</p>
            <button 
              onClick={() => refetch()}
              className="px-6 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.userName || "User";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Profile Settings</h1>
              <p className="text-gray-600 text-lg">Manage your personal and account information</p>
            </div>
            
            <div className="flex items-center gap-4">
              <div className={`px-4 py-2 rounded-full text-sm font-medium ${
                user.status === "Active" 
                  ? "bg-green-100 text-green-800 border border-green-200"
                  : "bg-yellow-100 text-yellow-800 border border-yellow-200"
              }`}>
                {user.status || "Active"}
              </div>
              <button
                onClick={() => editMode ? handleSave() : setEditMode(true)}
                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl"
              >
                {editMode ? (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes
                  </>
                ) : (
                  <>
                    <Edit2 className="w-4 h-4" />
                    Edit Profile
                  </>
                )}
              </button>
              {editMode && (
                <button
                  onClick={handleCancel}
                  className="px-5 py-2.5 bg-white text-gray-700 font-medium rounded-xl border-2 border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Success Message */}
          {showSaveMessage && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-5 mb-8 animate-fade-in">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-semibold text-green-900">Profile Updated Successfully</p>
                  <p className="text-sm text-green-800">Your changes have been saved.</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Left Sidebar - Profile Overview */}
          <div className="lg:col-span-1 space-y-6">
            {/* Profile Card */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-100 p-6 shadow-lg">
              <div className="text-center mb-6">
                <div className="w-24 h-24 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-xl">
                  <UserCircle className="w-12 h-12 text-white" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">{fullName}</h2>
                <p className="text-gray-600 text-sm mt-1">{user.occupation || "Professional"}</p>
                <div className="inline-flex items-center gap-1 mt-2 px-3 py-1 bg-white/50 rounded-full">
                  <BadgeCheck className="w-4 h-4 text-blue-600" />
                  <span className="text-xs text-blue-700 font-medium">Verified Account</span>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-white/70 rounded-xl">
                  <Mail className="w-4 h-4 text-blue-600" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="text-sm font-medium text-gray-900 truncate">{user.email || "Not set"}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 bg-white/70 rounded-xl">
                  <Phone className="w-4 h-4 text-blue-600" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500">Phone</p>
                    <p className="text-sm font-medium text-gray-900">{user.phone || "Not set"}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 bg-white/70 rounded-xl">
                  <Calendar className="w-4 h-4 text-blue-600" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500">Member Since</p>
                    <p className="text-sm font-medium text-gray-900">2023</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Tabs */}
            <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-lg">
              <nav className="space-y-1">
                <button
                  onClick={() => setActiveTab("personal")}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${
                    activeTab === "personal" 
                      ? "bg-blue-50 text-blue-700 border border-blue-100" 
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <User className="w-5 h-5" />
                  <div>
                    <p className="font-medium">Personal Info</p>
                    <p className="text-xs text-gray-500">Name, contact, address</p>
                  </div>
                </button>
                
                <button
                  onClick={() => setActiveTab("employment")}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${
                    activeTab === "employment" 
                      ? "bg-blue-50 text-blue-700 border border-blue-100" 
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <Briefcase className="w-5 h-5" />
                  <div>
                    <p className="font-medium">Employment</p>
                    <p className="text-xs text-gray-500">Occupation, NIC, DOB</p>
                  </div>
                </button>
                
                {/* <button
                  onClick={() => setActiveTab("banking")}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${
                    activeTab === "banking" 
                      ? "bg-blue-50 text-blue-700 border border-blue-100" 
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <Building className="w-5 h-5" />
                  <div>
                    <p className="font-medium">Bank Details</p>
                    <p className="text-xs text-gray-500">Account information</p>
                  </div>
                </button> */}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
              {/* Form Content */}
              <div className="p-8">
                {activeTab === "personal" && (
                  <form onSubmit={handleSave} className="space-y-8">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                        <User className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900">Personal Information</h2>
                        <p className="text-gray-600">Update your personal details</p>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      {/* First Name */}
                      <div className="group">
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                          First Name <span className="text-red-500">*</span>
                        </label>
                        {editMode ? (
                          <>
                            <input
                              type="text"
                              name="firstName"
                              value={user.firstName}
                              onChange={handleChange}
                              className={`w-full px-4 py-3 bg-gray-50 border-2 ${
                                formErrors.firstName ? 'border-red-300' : 'border-gray-200'
                              } text-gray-900 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all`}
                            />
                            {formErrors.firstName && (
                              <p className="text-red-600 text-sm mt-2">{formErrors.firstName}</p>
                            )}
                          </>
                        ) : (
                          <div className="px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl">
                            <p className="text-gray-900">{user.firstName || "Not provided"}</p>
                          </div>
                        )}
                      </div>

                      {/* Last Name */}
                      <div className="group">
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                          Last Name <span className="text-red-500">*</span>
                        </label>
                        {editMode ? (
                          <>
                            <input
                              type="text"
                              name="lastName"
                              value={user.lastName}
                              onChange={handleChange}
                              className={`w-full px-4 py-3 bg-gray-50 border-2 ${
                                formErrors.lastName ? 'border-red-300' : 'border-gray-200'
                              } text-gray-900 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all`}
                            />
                            {formErrors.lastName && (
                              <p className="text-red-600 text-sm mt-2">{formErrors.lastName}</p>
                            )}
                          </>
                        ) : (
                          <div className="px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl">
                            <p className="text-gray-900">{user.lastName || "Not provided"}</p>
                          </div>
                        )}
                      </div>

                       {/* Username */}
                      <div className="group">
                        <label className="block text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                          <Globe className="w-4 h-4" />
                          Username <span className="text-red-500">*</span>
                        </label>
                        {editMode ? (
                          <>
                            <input
                              type="text"
                              name="userName"
                              value={user.userName}
                              onChange={handleChange}
                              className={`w-full px-4 py-3 bg-gray-50 border-2 ${
                                formErrors.userName ? 'border-red-300' : 'border-gray-200'
                              } text-gray-900 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all`}
                            />
                            <p className="text-xs text-gray-500 mt-2">Used for logging in</p>
                            {formErrors.userName && (
                              <p className="text-red-600 text-sm mt-2">{formErrors.userName}</p>
                            )}
                          </>
                        ) : (
                          <div className="px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl">
                            <p className="text-gray-900">{user.userName || "Not provided"}</p>
                          </div>
                        )}
                      </div>

                      {/* Email */}
                      <div className="group">
                        <label className="block text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          Email Address <span className="text-red-500">*</span>
                        </label>
                        {editMode ? (
                          <>
                            <input
                              type="email"
                              name="email"
                              value={user.email}
                              onChange={handleChange}
                              className={`w-full px-4 py-3 bg-gray-50 border-2 ${
                                formErrors.email ? 'border-red-300' : 'border-gray-200'
                              } text-gray-900 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all`}
                            />
                            {formErrors.email && (
                              <p className="text-red-600 text-sm mt-2">{formErrors.email}</p>
                            )}
                          </>
                        ) : (
                          <div className="px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl">
                            <p className="text-gray-900">{user.email || "Not provided"}</p>
                          </div>
                        )}
                      </div>

                      {/* Phone */}
                      <div className="group">
                        <label className="block text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          Phone Number <span className="text-red-500">*</span>
                        </label>
                        {editMode ? (
                          <>
                            <input
                              type="tel"
                              name="phone"
                              value={user.phone}
                              onChange={handleChange}
                              className={`w-full px-4 py-3 bg-gray-50 border-2 ${
                                formErrors.phone ? 'border-red-300' : 'border-gray-200'
                              } text-gray-900 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all`}
                            />
                            {formErrors.phone && (
                              <p className="text-red-600 text-sm mt-2">{formErrors.phone}</p>
                            )}
                          </>
                        ) : (
                          <div className="px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl">
                            <p className="text-gray-900">{user.phone || "Not provided"}</p>
                          </div>
                        )}
                      </div>

                      {/* Address */}
                      <div className="group md:col-span-2">
                        <label className="block text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          Address <span className="text-red-500">*</span>
                        </label>
                        {editMode ? (
                          <>
                            <textarea
                              name="address"
                              value={user.address}
                              onChange={handleChange}
                              rows="3"
                              className={`w-full px-4 py-3 bg-gray-50 border-2 ${
                                formErrors.address ? 'border-red-300' : 'border-gray-200'
                              } text-gray-900 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all resize-none`}
                            />
                            {formErrors.address && (
                              <p className="text-red-600 text-sm mt-2">{formErrors.address}</p>
                            )}
                          </>
                        ) : (
                          <div className="px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl">
                            <p className="text-gray-900">{user.address || "Not provided"}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </form>
                )}

                {activeTab === "employment" && (
                  <form onSubmit={handleSave} className="space-y-8">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                        <Briefcase className="w-6 h-6 text-amber-600" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900">Employment Details</h2>
                        <p className="text-gray-600">Your professional information</p>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      {/* NIC */}
                      <div className="group">
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                          NIC / ID Number <span className="text-red-500">*</span>
                        </label>
                        {editMode ? (
                          <>
                            <input
                              type="text"
                              name="nic"
                              value={user.nic}
                              onChange={handleChange}
                              className={`w-full px-4 py-3 bg-gray-50 border-2 ${
                                formErrors.nic ? 'border-red-300' : 'border-gray-200'
                              } text-gray-900 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all`}
                            />
                            {formErrors.nic && (
                              <p className="text-red-600 text-sm mt-2">{formErrors.nic}</p>
                            )}
                          </>
                        ) : (
                          <div className="px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl">
                            <p className="text-gray-900">{user.nic || "Not provided"}</p>
                          </div>
                        )}
                      </div>

                      {/* Date of Birth */}
                      <div className="group">
                        <label className="block text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          Date of Birth <span className="text-red-500">*</span>
                        </label>
                        {editMode ? (
                          <>
                            <input
                              type="date"
                              name="dob"
                              value={user.dob || ""}
                              onChange={handleChange}
                              className={`w-full px-4 py-3 bg-gray-50 border-2 ${
                                formErrors.dob ? 'border-red-300' : 'border-gray-200'
                              } text-gray-900 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all`}
                            />
                            {formErrors.dob && (
                              <p className="text-red-600 text-sm mt-2">{formErrors.dob}</p>
                            )}
                          </>
                        ) : (
                          <div className="px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl">
                            <p className="text-gray-900">{formatDate(user.dob) || "Not provided"}</p>
                          </div>
                        )}
                      </div>

                      {/* Occupation */}
                      <div className="group md:col-span-2">
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                          Occupation
                        </label>
                        {editMode ? (
                          <input
                            type="text"
                            name="occupation"
                            value={user.occupation}
                            onChange={handleChange}
                            className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 text-gray-900 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all"
                          />
                        ) : (
                          <div className="px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl">
                            <p className="text-gray-900">{user.occupation || "Not provided"}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </form>
                )}

                {activeTab === "banking" && (
                  <form onSubmit={handleSave} className="space-y-8">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                        <Building className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900">Banking Information</h2>
                        <p className="text-gray-600">Your account details for transactions</p>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Bank Name */}
                      <div className="group">
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                          Bank Name
                        </label>
                        {editMode ? (
                          <input
                            type="text"
                            name="bankName"
                            value={user.bankName}
                            onChange={handleChange}
                            className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 text-gray-900 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all"
                          />
                        ) : (
                          <div className="px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl">
                            <p className="text-gray-900">{user.bankName || "Not provided"}</p>
                          </div>
                        )}
                      </div>

                      {/* Account Number */}
                      <div className="group">
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                          Account Number
                        </label>
                        {editMode ? (
                          <div className="relative">
                            <input
                              type={showPassword ? "text" : "password"}
                              name="accountNumber"
                              value={user.accountNumber}
                              onChange={handleChange}
                              className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 text-gray-900 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all pr-12"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                            >
                              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                          </div>
                        ) : (
                          <div className="px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl">
                            <p className="text-gray-900">
                              {user.accountNumber ? "••••••••" + user.accountNumber.slice(-4) : "Not provided"}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* IFSC Code */}
                      <div className="group">
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                          IFSC Code
                        </label>
                        {editMode ? (
                          <input
                            type="text"
                            name="ifscCode"
                            value={user.ifscCode}
                            onChange={handleChange}
                            className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 text-gray-900 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all"
                          />
                        ) : (
                          <div className="px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl">
                            <p className="text-gray-900">{user.ifscCode || "Not provided"}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Security Note */}
                    <div className="p-5 bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl border border-purple-100">
                      <div className="flex items-start gap-3">
                        <Shield className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm text-purple-800 font-medium mb-1">Security Note</p>
                          <p className="text-sm text-purple-700">
                            Your banking information is securely encrypted and stored. We never share your financial details with third parties.
                          </p>
                        </div>
                      </div>
                    </div>
                  </form>
                )}

                {/* Edit Mode Actions */}
                {editMode && (
                  <div className="pt-8 border-t border-gray-200 mt-8">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-600">
                        Make sure all information is accurate before saving
                      </p>
                      <div className="flex gap-4">
                        <button
                          type="button"
                          onClick={handleCancel}
                          className="px-6 py-3 bg-white text-gray-700 font-medium rounded-xl border-2 border-gray-300 hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSave}
                          disabled={isUpdatingBorrower || isUpdatingUser}
                          className={`px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl ${
                            (isUpdatingBorrower || isUpdatingUser) ? 'opacity-70 cursor-not-allowed' : ''
                          }`}
                        >
                          {isUpdatingBorrower || isUpdatingUser ? (
                            <span className="flex items-center gap-2">
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              Saving...
                            </span>
                          ) : (
                            'Save Changes'
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;